import React, { useState, useRef, useEffect, useContext, useCallback, useMemo } from "react";
import { useTheme } from "@material-ui/core/styles";

import { useHistory } from "react-router-dom";
import { format } from "date-fns";
// import { SocketContext } from "../../context/Socket/SocketContext";

import useSound from "use-sound";

import Popover from "@material-ui/core/Popover";
import IconButton from "@material-ui/core/IconButton";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import Avatar from "@material-ui/core/Avatar";
import Divider from "@material-ui/core/Divider";
import { makeStyles } from "@material-ui/core/styles";
import Badge from "@material-ui/core/Badge";
import ChatIcon from "@material-ui/icons/Chat";
import { TextRotateUp, TextRotationDown } from "@material-ui/icons";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import { Tooltip, Box, Typography } from "@material-ui/core";
import ToggleButton from "@material-ui/lab/ToggleButton";
import api from "../../services/api";

import useTickets from "../../hooks/useTickets";
import alertSound from "../../assets/sound.mp3";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import Favicon from "react-favicon";
import { getBackendUrl } from "../../config";
import defaultLogoFavicon from "../../assets/favicon.ico";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import {
	canUseBrowserNotifications,
	createAppNotification,
	requestBrowserNotificationPermission,
} from "../../utils/browserNotification";
import { syncWebPushSubscription } from "../../utils/webPush";

const useStyles = makeStyles(theme => ({
	tabContainer: {
		overflowY: "auto",
		maxHeight: 360,
		...theme.scrollbarStyles,
	},
	popoverPaper: {
		width: "100%",
		maxWidth: 340,
		marginLeft: theme.spacing(2),
		marginRight: theme.spacing(1),
		[theme.breakpoints.down("sm")]: {
			maxWidth: 290,
		},
	},
	noShadow: {
		boxShadow: "none !important",
	},
	customBadge: {
		backgroundColor: "#f44336",
		color: "white",
		top: 2,
		right: 1,
		border: `1px solid ${theme.palette.background.paper}`,
		height: 18,
		minWidth: 18,
		padding: '0 4px',
		fontSize: '0.7rem'
	},
	header: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		padding: theme.spacing(1, 1.5),
		borderBottom: `1px solid ${theme.palette.divider}`,
		backgroundColor: theme.palette.background.default,
	},
	sortButton: {
		padding: 4,
		borderRadius: 4,
		border: `1px solid ${theme.palette.divider}`,
		'&.Mui-selected': {
			backgroundColor: theme.palette.action.selected,
		}
	},
	compactItem: {
		padding: theme.spacing(1, 1.25),
		alignItems: "flex-start",
	},
	compactAvatar: {
		width: 36,
		height: 36,
	},
	compactTopRow: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: 8,
	},
	compactName: {
		fontWeight: 600,
		fontSize: "0.9rem",
		lineHeight: 1.2,
		color: theme.palette.text.primary,
	},
	compactTime: {
		fontSize: "0.75rem",
		color: theme.palette.text.secondary,
		whiteSpace: "nowrap",
	},
	compactMessage: {
		fontSize: "0.85rem",
		color: theme.palette.text.secondary,
		marginTop: 2,
		display: "-webkit-box",
		WebkitLineClamp: 2,
		WebkitBoxOrient: "vertical",
		overflow: "hidden",
		wordBreak: "break-word",
	},
	compactUnread: {
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		backgroundColor: theme.palette.success.main,
		color: theme.palette.common.white,
		fontSize: "0.7rem",
		fontWeight: 700,
		height: 18,
		minWidth: 18,
		padding: "0 5px",
		borderRadius: 10,
		marginTop: 6,
	}
}));

const NotificationsPopOver = (props) => {
	const volume = typeof props?.volume === "number" ? props.volume : (Number(props?.volume) || (typeof props === "number" ? props : 1));
	const classes = useStyles();
	const theme = useTheme();

	const history = useHistory();
	// const socketManager = useContext(SocketContext);
	const { user, socket } = useContext(AuthContext);
	const { profile, queues } = user;
	const sortStorageKey = `notifications.sortTickets.${user?.id || "default"}`;

	const ticketIdUrl = +history.location.pathname.split("/")[2];
	const ticketIdRef = useRef(ticketIdUrl);
	const anchorEl = useRef();
	const [isOpen, setIsOpen] = useState(false);
	const [notifications, setNotifications] = useState([]);
	const queueIds = queues.map((q) => q.id);
	const { get: getSetting } = useCompanySettings();
	const { setCurrentTicket, setTabOpen } = useContext(TicketsContext);

	const [showTicketWithoutQueue, setShowTicketWithoutQueue] = useState(() => user?.allTicket === "enable");
	const [showNotificationPending, setShowNotificationPending] = useState(true);
	const [showGroupNotification, setShowGroupNotification] = useState(() => Boolean(user?.allowGroup));

	const [, setDesktopNotifications] = useState([]);

	const [sortTickets, setSortTickets] = useState(() => {
		try {
			const userSaved = localStorage.getItem(sortStorageKey);
			if (userSaved !== null) {
				return userSaved === "true";
			}

			const legacySaved = localStorage.getItem("notifications.sortTickets.default");
			return legacySaved === "true";
		} catch (_) {
			return false;
		}
	});

	const { tickets } = useTickets({
		status: undefined,
		inboxScope: "true",
		withUnreadMessages: "true",
		sortTickets: sortTickets ? "ASC" : "DESC",
		debounceMs: 2000
	});

	const getInboxTabFromTicket = (ticket) => {
		if (ticket?.status === "pending" || ticket?.inboxStatus === "waiting") return "pending";
		if (ticket?.status === "closed" || ticket?.inboxStatus === "archived") return "closed";
		if (ticket?.status === "group" || ticket?.isGroup) return "group";
		if (ticket?.status === "open" || ticket?.inboxStatus === "active") return "open";
		return "pending";
	};

	const [play] = useSound(alertSound, { volume });
	const soundAlertRef = useRef();

	const historyRef = useRef(history);

	useEffect(() => {
		let isMounted = true;
		const fetchSettings = async () => {
			try {
				const setting = await getSetting(
					{
						"column": "showNotificationPending"
					}
				);

				if (!isMounted) return;
				const val = setting?.showNotificationPending;
				if (val === false || val === "false" || val === "disabled") {
					setShowNotificationPending(false);
				} else {
					setShowNotificationPending(true);
				}

				if (user.allTicket === "enable") {
					setShowTicketWithoutQueue(true);
				}
				if (user.allowGroup === true) {
					setShowGroupNotification(true);
				}
			} catch (err) {
				console.error("Error fetching notification settings:", err);
			}
		};

		fetchSettings();

		return () => {
			isMounted = false;
		};
	}, [user.allTicket, user.allowGroup]);

	useEffect(() => {
		soundAlertRef.current = play;

		if (!canUseBrowserNotifications()) {
			console.log("This browser doesn't support notifications");
		} else {
			requestBrowserNotificationPermission().then((permission) => {
				if (permission === "granted") {
					syncWebPushSubscription().catch(() => { });
				}
			});
		}
	}, [play]);

	useEffect(() => {
		if (!tickets) return;

		setNotifications(prevState => {
			const merged = new Map();
			// 1. Conservar notificaciones activas previas (ej. recibidas por socket en tiempo real)
			(prevState || []).forEach(item => {
				if (item?.id && Number(item?.unreadMessages || 1) > 0 && !item.isMuted) {
					merged.set(item.id, item);
				}
			});
			// 2. Incorporar tickets recibidos de la API con mensajes no leídos
			(tickets || []).forEach(item => {
				if (item?.id && Number(item?.unreadMessages) > 0 && !item.isMuted) {
					if (merged.has(item.id)) {
						merged.set(item.id, { ...merged.get(item.id), ...item });
					} else {
						merged.set(item.id, item);
					}
				} else if (item?.id && Number(item?.unreadMessages) === 0) {
					// Si la API confirma que ya no tiene mensajes no leídos, remover
					merged.delete(item.id);
				}
			});

			const next = Array.from(merged.values());
			next.sort((a, b) => {
				const timeA = new Date(a.updatedAt || 0).getTime();
				const timeB = new Date(b.updatedAt || 0).getTime();
				return sortTickets ? timeA - timeB : timeB - timeA;
			});

			return next.slice(0, 50);
		});
	}, [tickets, sortTickets]);

	useEffect(() => {
		try {
			const userSaved = localStorage.getItem(sortStorageKey);
			if (userSaved !== null) {
				setSortTickets(userSaved === "true");
				return;
			}

			const legacySaved = localStorage.getItem("notifications.sortTickets.default");
			if (legacySaved !== null) {
				setSortTickets(legacySaved === "true");
			}
		} catch (_) { }
	}, [sortStorageKey]);

	useEffect(() => {
		try {
			localStorage.setItem(sortStorageKey, String(sortTickets));
			localStorage.setItem("notifications.sortTickets.default", String(sortTickets));
		} catch (_) { }
	}, [sortStorageKey, sortTickets]);

	useEffect(() => {
		ticketIdRef.current = ticketIdUrl;
	}, [ticketIdUrl]);

	useEffect(() => {
		const companyId = user.companyId;
		// const socket = socketManager.GetSocket();
		if (user.id && socket) {
			const queueIds = queues.map((q) => q.id);

			const onConnectNotificationsPopover = () => {
				socket.emit("joinNotification");
			}

			const onCompanyTicketNotificationsPopover = (data) => {
				if (data.action === "updateUnread") {
					setNotifications(prevState => {
						const ticketIndex = prevState.findIndex(t => t.id === data.ticketId);
						if (ticketIndex !== -1) {
							prevState.splice(ticketIndex, 1);
							return [...prevState];
						}
						return prevState;
					});

					setDesktopNotifications(prevState => {
						const notfiticationIndex = prevState.findIndex(
							n => n.tag === String(data.ticketId)
						);
						if (notfiticationIndex !== -1) {
							prevState[notfiticationIndex].close();
							prevState.splice(notfiticationIndex, 1);
							return [...prevState];
						}
						return prevState;
					});
				}

				if (data.action === "update" && data.ticket?.id) {
					setNotifications(prevState => {
						const isExplicitlyRead = data.ticket.unreadMessages !== undefined && Number(data.ticket.unreadMessages) <= 0;
						const shouldRemove = isExplicitlyRead || !!data.ticket.isMuted;

						const ticketIndex = prevState.findIndex(t => t.id === data.ticket.id);

						if (shouldRemove) {
							if (ticketIndex !== -1) {
								prevState.splice(ticketIndex, 1);
								return [...prevState];
							}
							return prevState;
						}

						if (ticketIndex !== -1) {
							prevState[ticketIndex] = { ...prevState[ticketIndex], ...data.ticket };
							return [...prevState];
						}

						if (Number(data.ticket.unreadMessages || 0) > 0 && !data.ticket.isMuted) {
							return [data.ticket, ...prevState];
						}

						return prevState;
					});
				}
			};

			const onCompanyAppMessageNotificationsPopover = (data) => {
				if (
					data.action === "create" && !data.message.fromMe &&
					!data.message.read &&
					(data.ticket?.userId === user?.id || !data.ticket?.userId || user?.allTicket === "enable") &&
					(user?.allTicket === "enable" ||
						user?.queues?.some(queue => (queue.id === data.ticket.queueId)) ||
						!data.ticket.queueId && showTicketWithoutQueue === true) &&
					(!["pending", "lgpd", "nps", "group"].includes(data.ticket?.status) ||
						(data.ticket?.status === "pending" && showNotificationPending === true) ||
						(data.ticket?.status === "group" && showGroupNotification === true)) &&
					!data.ticket.isMuted
				) {
					const shouldBlurMessages = data.ticket.status === "pending" && user.allowSeeMessagesInPendingTickets === "disabled";

					const rawUnread = Number(data.ticket?.unreadMessages || 0);
					const ticketToAdd = {
						...data.ticket,
						unreadMessages: rawUnread > 0 ? rawUnread : 1,
						lastMessage: shouldBlurMessages
							? i18n.t("notifications.messageHidden")
							: (data.message?.body || data.ticket?.lastMessage || ""),
						updatedAt: data.message?.createdAt || data.ticket?.updatedAt || new Date().toISOString()
					};

					const shouldNotNotificate =
						(data.message.ticketId === ticketIdRef.current &&
							document.visibilityState === "visible") ||
						(data.ticket.userId && data.ticket.userId !== user?.id && user?.allTicket !== "enable") ||
						(data.ticket.isGroup && showGroupNotification === false);

					if (shouldNotNotificate === true) return;

					setNotifications(prevState => {
						const ticketIndex = prevState.findIndex(t => t.id === ticketToAdd.id);
						if (ticketIndex !== -1) {
							const existingUnread = Number(prevState[ticketIndex]?.unreadMessages || 0);
							prevState[ticketIndex] = {
								...prevState[ticketIndex],
								...ticketToAdd,
								unreadMessages: existingUnread > 0 ? existingUnread + 1 : ticketToAdd.unreadMessages
							};
							return [...prevState];
						}
						return [ticketToAdd, ...prevState];
					});

					const messageBody = shouldBlurMessages
						? i18n.t("notifications.messageHidden")
						: data.message.body;

					void handleNotifications({
						...data,
						ticket: ticketToAdd,
						message: {
							...data.message,
							body: messageBody
						}
					});
				}
			}

			socket.on("connect", onConnectNotificationsPopover);
			socket.on(`company-${companyId}-ticket`, onCompanyTicketNotificationsPopover);
			socket.on(`company-${companyId}-appMessage`, onCompanyAppMessageNotificationsPopover);

			return () => {
				socket.off("connect", onConnectNotificationsPopover);
				socket.off(`company-${companyId}-ticket`, onCompanyTicketNotificationsPopover);
				socket.off(`company-${companyId}-appMessage`, onCompanyAppMessageNotificationsPopover);
			};
		}
		return undefined;
	}, [user, profile, queues, showTicketWithoutQueue, socket, showNotificationPending, showGroupNotification]);

	const handleNotifications = useCallback(async (data) => {
		const { message, contact, ticket } = data;
		const targetTab = getInboxTabFromTicket(ticket);
		const targetUrl = `/tickets/${ticket.uuid}?tab=${targetTab}`;

		const notification = await createAppNotification({
			title:
			`${i18n.t("tickets.notification.message")} ${contact.name}`,
			body: `${message.body} - ${format(new Date(), "HH:mm")}`,
			icon: contact.urlPicture,
			tag: String(ticket.id),
			renotify: true,
			url: targetUrl,
			onClick: (e) => {
				e.preventDefault();
				window.focus();
				setNotifications((prevState) => prevState.filter((t) => t.id !== ticket.id));
				const isPendingTicket = ticket?.status === "pending" || ticket?.inboxStatus === "waiting";
				const isAlreadyActiveForMe =
					!isPendingTicket && ticket?.inboxStatus === "active" && ticket?.userId === user?.id;
				if (!isAlreadyActiveForMe) {
					api.put(`/tickets/${ticket.id}`, {
						scope: "inbox",
						unreadMessages: 0,
						status: isPendingTicket ? "pending" : ticket.status,
						inboxStatus: isPendingTicket ? "waiting" : ticket.inboxStatus
					}).catch(() => { });
				}
				const openTab = isAlreadyActiveForMe
					? (ticket?.isGroup ? "group" : "open")
					: targetTab;
				setTabOpen(openTab);
				historyRef.current.push(`/tickets/${ticket.uuid}?tab=${openTab}`);
			},
		});

		if (notification) {
			setDesktopNotifications(prevState => {
				const notfiticationIndex = prevState.findIndex(
					n => n.tag === notification.tag
				);
				if (notfiticationIndex !== -1) {
					prevState[notfiticationIndex] = notification;
					return [...prevState];
				}
				return [notification, ...prevState];
			});
		}
		if (!ticket.isMuted) {
			soundAlertRef.current();
		}
	}, [user]);

	const handleClick = () => {
		setIsOpen(prevState => !prevState);
	};

	const handleClickAway = () => {
		setIsOpen(false);
	};

	const handleOpenNotificationTicket = (ticket) => {
		setNotifications(prevState => prevState.filter(t => t.id !== ticket.id));
		const isPendingTicket = ticket?.status === "pending" || ticket?.inboxStatus === "waiting";
		const isAlreadyActiveForMe =
			!isPendingTicket && ticket?.inboxStatus === "active" && ticket?.userId === user?.id;
		if (!isAlreadyActiveForMe) {
			api.put(`/tickets/${ticket.id}`, {
				scope: "inbox",
				unreadMessages: 0,
				status: isPendingTicket ? "pending" : ticket.status,
				inboxStatus: isPendingTicket ? "waiting" : ticket.inboxStatus
			}).catch(() => { });
		}
		const targetTab = isAlreadyActiveForMe
			? (ticket?.isGroup ? "group" : "open")
			: getInboxTabFromTicket(ticket);
		setTabOpen(targetTab);
		setCurrentTicket({ id: ticket.id, uuid: ticket.uuid });
		history.push(`/tickets/${ticket.uuid}?tab=${targetTab}`);
		handleClickAway();
	};

	const handleMarkAllAsRead = useCallback(async () => {
		try {
			await Promise.all(notifications.map(ticket =>
				api.put(`/tickets/${ticket.id}`, { 
					unreadMessages: 0,
					status: ticket.status,
					inboxStatus: ticket.inboxStatus
				})
			));
			setNotifications([]);
		} catch (err) {
			toastError(err);
		}
	}, [notifications]);

	const formatNotificationTime = (dateValue) => {
		try {
			return format(new Date(dateValue), "HH:mm");
		} catch (_) {
			return "";
		}
	};

	const browserNotification = useMemo(() => {
		const numbers = "⓿➊➋➌➍➎➏➐➑➒➓⓫⓬⓭⓮⓯⓰⓱⓲⓳⓴";
		if (notifications.length > 0) {
			if (notifications.length < 21) {
				document.title = numbers.substring(notifications.length, notifications.length + 1) + " - " + (theme.appName || "...");
			} else {
				document.title = "(" + notifications.length + ")" + (theme.appName || "...");
			}
		} else {
			document.title = theme.appName || "...";
		}
		return (
			<Favicon
				animated={true}
				url={(theme?.appLogoFavicon) ? theme.appLogoFavicon : defaultLogoFavicon}
				alertCount={notifications.length}
				iconSize={195}
			/>
		);
	}, [notifications.length, theme.appName, theme.appLogoFavicon]);

	return (
		<>
			{browserNotification}

			<IconButton
				onClick={handleClick}
				ref={anchorEl}
				aria-label="Notificaciones"
				color="inherit"
				style={{ color: "white" }}
			>
				<Badge overlap="rectangular" badgeContent={notifications.length} classes={{ badge: classes.customBadge }}>
					<ChatIcon />
				</Badge>
			</IconButton>
			<Popover
				disableScrollLock
				open={isOpen}
				anchorEl={anchorEl.current}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				classes={{ paper: classes.popoverPaper }}
				onClose={handleClickAway}
			>
				<Box className={classes.header}>
					<Typography variant="subtitle1" style={{ fontWeight: 600 }}>
						{i18n.t("notifications.recentMessages")}
					</Typography>
					<Box style={{ display: 'flex', alignItems: 'center' }}>
						<Tooltip title="Marcar todo como leído">
							<IconButton size="small" onClick={handleMarkAllAsRead} style={{ marginRight: 8 }}>
								<DoneAllIcon fontSize="small" />
							</IconButton>
						</Tooltip>
						<Tooltip title={sortTickets ? "Antiguos primero" : "Recientes primero"}>
							<ToggleButton
								value="check"
								selected={sortTickets}
								onChange={() => setSortTickets(prev => !prev)}
								className={classes.sortButton}
								size="small"
							>
								{sortTickets ? (
									<TextRotateUp fontSize="small" />
								) : (
									<TextRotationDown fontSize="small" />
								)}
							</ToggleButton>
						</Tooltip>
					</Box>
				</Box>
				<List dense className={classes.tabContainer}>
					{notifications.length === 0 ? (
						<ListItem>
							<ListItemText>{i18n.t("notifications.noTickets")}</ListItemText>
						</ListItem>
					) : (
						notifications.slice(0, 50).map((ticket, index) => (
							<React.Fragment key={ticket.id}>
								<ListItem
									button
									className={classes.compactItem}
									onClick={() => handleOpenNotificationTicket(ticket)}
								>
									<ListItemAvatar>
										<Avatar
											src={ticket.contact?.urlPicture}
											className={classes.compactAvatar}
										>
											{ticket.contact?.name?.charAt(0)?.toUpperCase() || "?"}
										</Avatar>
									</ListItemAvatar>
									<div style={{ width: "100%", minWidth: 0 }}>
										<div className={classes.compactTopRow}>
											<Typography className={classes.compactName} noWrap>
												{ticket.contact?.name || `#${ticket.id}`}
											</Typography>
											<Typography className={classes.compactTime}>
												{formatNotificationTime(ticket.updatedAt)}
											</Typography>
										</div>
										<Typography className={classes.compactMessage}>
											{ticket.lastMessage || i18n.t("notifications.noTickets")}
										</Typography>
										{ticket.unreadMessages > 0 && (
											<span className={classes.compactUnread}>
												{ticket.unreadMessages}
											</span>
										)}
									</div>
								</ListItem>
								{index < notifications.slice(0, 50).length - 1 && <Divider component="li" />}
							</React.Fragment>
						))
					)}
				</List>
			</Popover>
		</>
	);
};

export default NotificationsPopOver;
